import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiMessageSquare, FiDatabase, FiFileText, FiSettings, FiGlobe, FiClipboard, FiScan, FiArrowRight } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiCalendar,
      title: 'Appointment Booking Automation',
      description: 'Automate your scheduling with 24/7 online booking. Reduce no-shows by 60% and save 15+ hours weekly. Includes FREE professional website and hosting.',
      features: ['24/7 Online Booking', 'Automated SMS/Email Reminders', 'Calendar Integration', 'FREE Website & Hosting'],
      gradient: 'from-blue-500 to-purple-600',
      link: '/services/appointment-booking',
      savings: 'Save 15+ hours/week'
    },
    {
      icon: FiMessageSquare,
      title: 'AI Chatbots & Virtual Assistants',
      description: 'Deploy intelligent AI chatbots that handle 80% of customer inquiries automatically. Qualify leads 24/7 and boost conversions by 300%.',
      features: ['Natural Language AI', 'Lead Qualification', '24/7 Customer Support', 'Multi-Platform Integration'],
      gradient: 'from-green-500 to-teal-600',
      link: '/services/ai-chatbots',
      savings: 'From $200 only'
    },
    {
      icon: FiDatabase,
      title: 'CRM Automation',
      description: 'Automate your sales pipeline with smart CRM workflows. Increase conversion rates by 45% and never miss a follow-up again.',
      features: ['Lead Scoring', 'Automated Follow-ups', 'Pipeline Management', 'Sales Analytics'],
      gradient: 'from-orange-500 to-red-600',
      link: '/services/crm-automation',
      savings: '45% more conversions'
    },
    {
      icon: FiFileText,
      title: 'Document Generation & Automation',
      description: 'Generate professional documents automatically from templates. Reduce document creation time by 95% and eliminate errors.',
      features: ['Template Automation', 'Digital Signatures', 'Data Integration', 'Compliance Ready'],
      gradient: 'from-purple-500 to-pink-600',
      link: '/services/document-generation',
      savings: '95% time reduction'
    },
    {
      icon: FiSettings,
      title: 'Microsoft 365 Automation',
      description: 'Leverage Microsoft Power Platform to automate workflows across Teams, SharePoint, Outlook and more. Boost team productivity.',
      features: ['Power Automate Flows', 'SharePoint Integration', 'Teams Automation', 'Outlook Workflows'],
      gradient: 'from-indigo-500 to-blue-600',
      link: '/services/microsoft-automation',
      savings: 'Enterprise ready'
    },
    {
      icon: FiGlobe,
      title: 'Professional Website Design',
      description: 'Modern, responsive websites that convert visitors to customers. SEO optimized with integrated booking systems and fast loading.',
      features: ['Responsive Design', 'SEO Optimization', 'Booking Integration', 'E-commerce Ready'],
      gradient: 'from-cyan-500 to-blue-600',
      link: '/services/website-designing',
      savings: '150% more leads'
    },
    {
      icon: FiClipboard,
      title: 'Smart Forms & Surveys',
      description: 'Create intelligent forms with conditional logic. Increase completion rates by 40% and automate data collection seamlessly.',
      features: ['Dynamic Forms', 'Data Validation', 'Analytics Dashboard', 'CRM Integration'],
      gradient: 'from-emerald-500 to-green-600',
      link: '/services/online-forms',
      savings: '40% higher completion'
    },
    {
      icon: FiScan,
      title: 'Document Scanning & OCR',
      description: 'Digitize physical documents with advanced OCR. Create searchable archives and reduce processing time by 90%.',
      features: ['High-Quality Scanning', 'Text Recognition', 'Searchable Archives', 'Automated Filing'],
      gradient: 'from-violet-500 to-purple-600',
      link: '/services/scanning-ocr',
      savings: '90% faster processing'
    },
    {
      icon: FiArrowRight,
      title: 'Custom Process Automation',
      description: 'Tailored automation for your unique business needs. Reduce operational costs by 70% with industry-specific solutions.',
      features: ['Custom Workflows', 'API Integrations', 'Scalable Solutions', '24/7 Support'],
      gradient: 'from-gray-700 to-gray-900',
      link: '/services/custom-process-automation',
      savings: '70% cost reduction'
    }
  ];

  return (
    <section id="services" className="py-12 md:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-10 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
            Automation Solutions for <span className="text-primary-600">Every Business</span>
          </h2>
          <div className="w-16 md:w-20 h-1 bg-primary-600 mx-auto mb-4 md:mb-6"></div>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Proven automation solutions trusted by 500+ Australian businesses. Save costs, boost efficiency, and scale operations effortlessly.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group bg-white rounded-xl md:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 relative flex flex-col h-full"
            >
              {/* Savings Badge */}
              <div className="absolute top-3 right-3 md:top-4 md:right-4 bg-green-500 text-white px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-semibold z-10">
                {service.savings}
              </div>

              {/* Header with Gradient */}
              <div className={`bg-gradient-to-br ${service.gradient} p-4 md:p-6 text-white relative overflow-hidden`}>
                <div className="absolute top-0 right-0 w-24 h-24 md:w-32 md:h-32 bg-white/10 rounded-full -translate-y-12 translate-x-12 md:-translate-y-16 md:translate-x-16"></div>
                <div className="relative z-10">
                  <SafeIcon icon={service.icon} className="h-10 w-10 md:h-12 md:w-12 mb-3 md:mb-4" />
                  <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-2">{service.title}</h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 md:p-6 flex-grow flex flex-col">
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6 flex-grow">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-xs md:text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-primary-600 rounded-full mr-2 md:mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  to={service.link}
                  className="w-full bg-gray-100 text-gray-800 py-2 md:py-3 rounded-lg hover:bg-primary-600 hover:text-white transition-all duration-300 font-semibold group-hover:bg-primary-600 group-hover:text-white inline-flex items-center justify-center space-x-2 text-sm md:text-base"
                >
                  <span>Learn More</span>
                  <SafeIcon icon={FiArrowRight} className="h-3 w-3 md:h-4 md:w-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-12 md:mt-16"
        >
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl md:rounded-2xl p-6 md:p-8 lg:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">Ready to Save 70% Costs with Automation?</h3>
            <p className="text-base md:text-xl mb-6 md:mb-8 text-primary-100">
              Join 500+ Australian businesses already saving time and money with our proven automation solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://tidycal.com/anishmmalhotra/initial-20-minutes-discussion"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-white text-primary-600 px-6 py-3 md:px-8 md:py-4 rounded-lg hover:bg-gray-100 transition-all duration-300 font-semibold text-base md:text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Get FREE Automation Audit</span>
                <SafeIcon icon={FiArrowRight} className="h-4 w-4 md:h-5 md:w-5" />
              </a>
              <button
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center space-x-2 border-2 border-white text-white px-6 py-3 md:px-8 md:py-4 rounded-lg hover:bg-white hover:text-primary-600 transition-all duration-300 font-semibold text-base md:text-lg"
              >
                <span>View Case Studies</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;